'use client';

import React, { useEffect, useRef } from 'react';
import AppImage from '@/components/ui/AppImage';
import { useLang } from './LanguageContext';

export default function HeroSection() {
  const { t } = useLang();
  const titleRef = useRef<HTMLHeadingElement>(null);

  useEffect(() => {
    // Stagger animation on mount
    const el = document.getElementById('hero-content');
    if (el) {
      el.style.opacity = '1';
    }
  }, []);

  const titleLines = t('hero_title')?.split('\n');

  return (
    <section
      id="hero"
      className="relative min-h-screen flex items-center overflow-hidden">
      
      {/* Background image with Ken Burns */}
      <div className="absolute inset-0 z-0">
        <div className="w-full h-full overflow-hidden">
          <AppImage
            src="https://images.unsplash.com/photo-1651063820152-d3e7a27b4d2b"
            alt="Dubai skyline at sunset with Burj Khalifa and city lights reflecting on water"
            fill
            className="object-cover animate-ken-burns"
            priority />
          
        </div>
        <div className="absolute inset-0 hero-gradient" />
        <div className="absolute inset-0 bg-gradient-to-b from-navy/30 via-transparent to-navy" />
      </div>
      {/* Floating decorative orbs */}
      <div
        className="absolute top-1/4 right-1/4 w-64 h-64 rounded-full opacity-10 pointer-events-none"
        style={{ background: 'radial-gradient(circle, #C9A84C 0%, transparent 70%)' }} />
      
      <div
        className="absolute bottom-1/3 left-1/3 w-96 h-96 rounded-full opacity-5 pointer-events-none"
        style={{ background: 'radial-gradient(circle, #C4622D 0%, transparent 70%)' }} />
      
      {/* Content */}
      <div
        id="hero-content"
        className="relative z-10 max-w-8xl mx-auto px-6 md:px-10 pt-28 pb-20 w-full"
        style={{ opacity: 0, transition: 'opacity 0.8s ease' }}>
        
        <div className="max-w-3xl">
          {/* Label */}
          <div
            className="section-label mb-6 animate-fade-in-up"
            style={{ animationDelay: '0.2s', animationFillMode: 'both' }}>
            
            {t('hero_label')}
          </div>

          {/* Title */}
          <h1
            ref={titleRef}
            className="font-serif font-bold text-cream leading-[1.0] mb-6"
            style={{
              fontFamily: 'Fraunces, serif',
              fontSize: 'clamp(3rem, 8vw, 7rem)',
              animationFillMode: 'both'
            }}>
            
            {titleLines?.map((line, i) =>
            <span
              key={i}
              className="block animate-fade-in-up"
              style={{ animationDelay: `${0.3 + i * 0.15}s`, animationFillMode: 'both' }}>
              
                {i === 1 ?
              <>
                    <span className="text-shimmer">{line?.split(' ')?.slice(0, 2)?.join(' ')}</span>
                    {' '}{line?.split(' ')?.slice(2)?.join(' ')}
                  </> :
              line}
              </span>
            )}
          </h1>

          {/* Subtitle */}
          <p
            className="text-cream/70 text-lg md:text-xl font-light leading-relaxed mb-10 max-w-lg animate-fade-in-up"
            style={{ animationDelay: '0.6s', animationFillMode: 'both' }}>
            
            {t('hero_subtitle')}
          </p>

          {/* CTAs */}
          <div
            className="flex flex-wrap gap-4 mb-16 animate-fade-in-up"
            style={{ animationDelay: '0.75s', animationFillMode: 'both' }}>
            
            <a href="#destinations" className="btn-primary">
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                <path d="M12 22s-8-4.5-8-11.8A8 8 0 0 1 12 2a8 8 0 0 1 8 8.2c0 7.3-8 11.8-8 11.8z" />
                <circle cx="12" cy="10" r="3" />
              </svg>
              {t('hero_cta')}
            </a>
            <a href="#contact" className="btn-outline">
              {t('hero_cta2')}
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                <path d="M5 12h14M12 5l7 7-7 7" />
              </svg>
            </a>
          </div>

          {/* Stats */}
          <div
            className="flex flex-wrap gap-8 animate-fade-in-up"
            style={{ animationDelay: '0.9s', animationFillMode: 'both' }}>
            
            {[
            { num: t('hero_stat1_num'), label: t('hero_stat1_label') },
            { num: t('hero_stat2_num'), label: t('hero_stat2_label') },
            { num: t('hero_stat3_num'), label: t('hero_stat3_label') }]?.
            map((stat, i) =>
            <div key={i} className="stat-card">
                <div
                className="text-2xl md:text-3xl font-serif font-bold text-shimmer"
                style={{ fontFamily: 'Fraunces, serif' }}>
                
                  {stat?.num}
                </div>
                <div className="text-cream/50 text-xs font-medium tracking-wider uppercase mt-1">
                  {stat?.label}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
      {/* Scroll indicator */}
      <div className="absolute bottom-10 left-1/2 -translate-x-1/2 z-10 flex flex-col items-center gap-3">
        <span className="text-cream/30 text-xs tracking-widest uppercase">Scroll</span>
        <div className="w-px h-12 bg-white/10 relative overflow-hidden">
          <div className="absolute inset-0 bg-gold animate-scroll-line" />
        </div>
      </div>
      {/* Floating card — top destination teaser */}
      <div
        className="absolute bottom-16 right-8 md:right-16 z-10 glass rounded-2xl p-4 hidden md:block animate-float"
        style={{ animationDelay: '1s' }}>
        
        <div className="flex items-center gap-3">
          <div className="w-12 h-12 rounded-xl overflow-hidden flex-shrink-0">
            <AppImage
              src="https://images.unsplash.com/photo-1726910921322-9032a3f3240b"
              alt="Registan Square Samarkand blue domes"
              width={48}
              height={48}
              className="object-cover w-full h-full" />
            
          </div>
          <div>
            <div className="text-gold text-xs font-bold tracking-wider uppercase">Samarkand</div>
            <div className="text-cream/60 text-xs mt-0.5">Silk Road Magic</div>
          </div>
          <div className="ml-4 text-gold">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
              <path d="M5 12h14M12 5l7 7-7 7" />
            </svg>
          </div>
        </div>
      </div>
    </section>);

}